/**
 * Client for POST /api/books/picture-book-suggestions (ruling P2-R6): the call is aborted at 3 000 ms and any
 * failure (timeout, network, 404 while the flag is off, 429, a malformed answer) gives the same deterministic
 * fallback the server uses, so the parent never waits more than 3 s. Never throws; never logs names.
 */
import type { PictureBookStyle } from '@/types/manga';
import type { ReaderBand } from '@/utils/manga/comic-story-profile';
import type { PhotoScene } from '@/utils/picture-book/photo-scene';
import { PICTURE_BOOK_FLOW_STYLES } from '@/utils/manga/picture-book-style';
import { fallbackSuggestion, type SuggestionPerson } from '@/utils/picture-book/suggestion-fallback';

export const SUGGESTION_CLIENT_TIMEOUT_MS = 3_000;

export interface SuggestionRequest {
  characters: SuggestionPerson[];
  band: ReaderBand;
  language: string;
  scene?: PhotoScene | null;
  shuffle?: number;
  keepTitle?: string;
  avoidIdeas?: string[];
}

export interface SuggestionAnswer {
  style: PictureBookStyle;
  title: string;
  idea: string;
  source: 'llm' | 'fallback';
}

/** The server's fallback semantics: the band's pool entry, with the parent's title kept when one was sent. */
function clientFallback(body: SuggestionRequest): SuggestionAnswer {
  const fb = fallbackSuggestion({ people: body.characters, band: body.band, scene: body.scene, shuffle: body.shuffle });
  return { ...fb, title: body.keepTitle?.trim() || fb.title, source: 'fallback' };
}

function parseAnswer(j: any): SuggestionAnswer | null {
  if (!j || typeof j !== 'object') return null;
  if (!(PICTURE_BOOK_FLOW_STYLES as readonly string[]).includes(j.style)) return null;
  if (typeof j.title !== 'string' || !j.title.trim() || typeof j.idea !== 'string' || !j.idea.trim()) return null;
  return { style: j.style, title: j.title, idea: j.idea, source: j.source === 'llm' ? 'llm' : 'fallback' };
}

export async function fetchSuggestion(
  body: SuggestionRequest,
  opts: { timeoutMs?: number; fetchFn?: typeof fetch } = {},
): Promise<SuggestionAnswer> {
  const fetchFn = opts.fetchFn ?? fetch;
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => {
        controller.abort();
        resolve(null);
      }, opts.timeoutMs ?? SUGGESTION_CLIENT_TIMEOUT_MS);
    });
    const call = (async () => {
      const res = await fetchFn('/api/books/picture-book-suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!res.ok) return null;
      return parseAnswer(await res.json());
    })().catch(() => null); // an abort after the timeout must not surface as an unhandled rejection
    return (await Promise.race([call, timeout])) ?? clientFallback(body);
  } catch {
    return clientFallback(body);
  } finally {
    if (timer) clearTimeout(timer);
  }
}
